import { type RefObject, useLayoutEffect, useState } from "react";

/**
 * Px from the visual zone's foot to where the turntable's robot should end:
 * the hint and tab bar below it, and the hint's gap to the bar again above
 * it, so the hint sits centred between. On mobile, where the hint is hidden,
 * the robot keeps the gap the hint would have kept from the bar. Measured
 * before paint, so the robot never shows behind the bar first; null until
 * then.
 */
export const useTurntableClearance = (
  {
    foot,
    hint,
    bar,
  }: {
    /** The zone's foot, holding the hint and the bar. */
    foot: RefObject<HTMLElement | null>;
    hint: RefObject<HTMLElement | null>;
    bar: RefObject<HTMLElement | null>;
  },
  enabled: boolean,
): number | null => {
  const [clearance, setClearance] = useState<number | null>(null);

  useLayoutEffect(() => {
    const footEl = foot.current;
    const hintEl = hint.current;
    const barEl = bar.current;
    const zone = footEl?.offsetParent;
    if (
      !enabled ||
      footEl == null ||
      hintEl == null ||
      barEl == null ||
      !(zone instanceof HTMLElement)
    )
      return;
    const measure = () => {
      const zoneBottom = zone.getBoundingClientRect().bottom;
      const barTop = barEl.getBoundingClientRect().top;
      const hintStyle = getComputedStyle(hintEl);
      if (hintStyle.display === "none") {
        const gap =
          parseFloat(hintStyle.marginBottom) +
          parseFloat(getComputedStyle(footEl).rowGap);
        setClearance(zoneBottom - barTop + gap);
        return;
      }
      const hintBox = hintEl.getBoundingClientRect();
      setClearance(zoneBottom - hintBox.top + (barTop - hintBox.bottom));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(footEl);
    observer.observe(zone);
    return () => observer.disconnect();
  }, [foot, hint, bar, enabled]);

  return clearance;
};
